/**
 * Статусы турнира.
 * @readonly
 * @enum {string}
 */
export const TournamentStatus = {
  CREATED: 'created',
  ONGOING: 'ongoing',
  FINISHED: 'finished',
};

/**
 * Этапы турнира.
 * @readonly
 * @enum {string}
 */
export const TournamentStage = {
  REGULAR: 'regular',
  ELIMINATION: 'elimination',
};

/**
 * Статусы участия игрока в турнире.
 * @readonly
 * @enum {string}
 */
export const TournamentParticipantStatus = {
  ACTIVE: 'active',
  WITHDRAWN: 'withdrawn',
  REPLACED: 'replaced',
  SUBSTITUTED: 'substituted',
};

/**
 * Фазы турнира для фиксации момента выбытия участника.
 * @readonly
 * @enum {string}
 */
export const TournamentParticipantPhase = {
  REGULAR: 'regular',
  ELIMINATION: 'elimination',
};

/**
 * Категории матчей.
 * @readonly
 * @enum {string}
 */
export const GameCategory = {
  REGULAR: "regular",
  ELIMINATION_TOP: "elimination-top",
  ELIMINATION_BOTTOM: "elimination-bottom",
  OTHER: "other"
};

/**
 * Системы проведения турниров.
 * @readonly
 * @enum {string}
 */
export const TournamentSystem = {
  ROUND_ROBIN: 'round-robin',
  SWISS: 'swiss',
  ELIMINATION: 'elimination',
  MIXED: 'mixed'
};

/**
 * Человеко-читаемые подписи систем проведения турниров.
 * @readonly
 * @enum {string}
 */
export const TournamentSystemLabel = {
  [TournamentSystem.ROUND_ROBIN]: 'Круговой',
  [TournamentSystem.SWISS]: 'Швейцарка',
  [TournamentSystem.ELIMINATION]: 'Плей-офф',
  [TournamentSystem.MIXED]: 'Смешанная',
};

/**
 * Режимы расчёта рейтинга в турнире.
 * @readonly
 * @enum {string}
 */
export const TournamentCalculateType = {
  FROZEN: 'frozen',
  ROLLING: 'rolling',
};

/**
 * Форматы соревнования.
 * @readonly
 * @enum {string}
 */
export const CompetitionFormat = {
  INDIVIDUAL: 'individual',
  TEAM: 'team',
};

/**
 * Режимы командного турнира.
 * @readonly
 * @enum {string}
 */
export const TeamMode = {
  FIXED: 'fixed',
  RANDOM: 'random',
};

/**
 * Режимы жеребьёвки между командами.
 * @readonly
 * @enum {string}
 */
export const TeamPairingMode = {
  AUTO: 'auto',
  MANUAL: 'manual',
};

/**
 * Форматы командного матча.
 * @readonly
 * @enum {string}
 */
export const TeamMatchFormat = {
  BOARD_SUM: 'board-sum',
  COMMON_BOARD: 'common-board',
  FIRST_WIN: 'first-win',
};

/**
 * Режимы формирования временных команд.
 * @readonly
 * @enum {string}
 */
export const RandomTeamFormationMode = {
  RANDOM: 'random',
  BALANCED: 'balanced',
  SCORE_GROUPS: 'score-groups',
};

/**
 * Типы карточек аккордеона партий.
 * @readonly
 * @enum {string}
 */
export const GAItemKind = {
  INDIVIDUAL: 'individual',
  TEAM: 'team',
};

/**
 * Бизнес-форматы карточек аккордеона партий.
 * @readonly
 * @enum {string}
 */
export const GAItemFormat = {
  INDIVIDUAL: 'individual',
  COMMON_BOARD: TeamMatchFormat.COMMON_BOARD,
  BOARD_SUM: TeamMatchFormat.BOARD_SUM,
  FIRST_WIN: TeamMatchFormat.FIRST_WIN,
};

/**
 * Режимы отображения тела карточки аккордеона партий.
 * @readonly
 * @enum {string}
 */
export const GABodyMode = {
  PLAYER_VS_PLAYER: 'player-vs-player',
  TEAM_MEMBERS_VS_TEAM_MEMBERS: 'team-members-vs-team-members',
  TEAM_LINEUPS: 'team-lineups',
  TEAM_BOARD_GAMES: 'team-board-games',
  NONE: 'none',
};

/**
 * Единицы подсчёта командной серии.
 * @readonly
 * @enum {string}
 */
export const TeamScoringUnit = {
  POINTS: 'points',
  SETS: 'sets',
};

/**
 * Режимы участия в плей-офф.
 * @readonly
 * @enum {string}
 */
export const ParticipantsMode = {
  ALL: 'all',
  LIMIT: 'limit',
  SPLIT: 'split',
  MANUAL_MANUAL: 'manual-manual',
  MANUAL_REMAINING: 'manual-remaining'
};

/**
 * Стадии матчей в плей-офф (включая нижнюю сетку).
 * @readonly
 * @enum {string}
 */
export const GameStage = {
  REGULAR: 'REGULAR',
  R64: 'R64',   // 1/64
  LR64: 'LR64', // 1/64 нижняя сетка
  R32: 'R32',   // 1/32
  LR32: 'LR32', // 1/32 нижняя сетка
  R16: 'R16',   // 1/16
  LR16: 'LR16', // 1/16 нижняя сетка
  R8:  'R8',    // 1/8
  LR8:  'LR8',  // 1/8 нижняя сетка
  QF:  'QF',    // Четвертьфинал
  LQF:  'LQF',  // Четвертьфинал нижняя сетка
  SF:  'SF',    // Полуфинал
  LSF:  'LSF',  // Полуфинал нижняя сетка
  F:   'F',     // Финал
  LF:   'LF',   // Финал нижней сетки
  P3:  'P3',    // Матч за 3-е место
  LP3:  'LP3'   // Матч за 3-е место нижней сетки
};

/**
 * Человеко-читаемые подписи стадий матчей.
 * @readonly
 * @enum {string}
 */
export const GameStageLabel = {
  REGULAR: 'Регулярная часть',
  R64: '1/64 финала',
  LR64: '1/64 нижняя сетка',
  R32: '1/32 финала',
  LR32: '1/32 нижняя сетка',
  R16: '1/16 финала',
  LR16: '1/16 нижняя сетка',
  R8:  '1/8 финала',
  LR8:  '1/8 нижняя сетка',
  QF:  'Четвертьфинал',
  LQF: 'Четвертьфинал (н)',
  SF:  'Полуфинал',
  LSF: 'Полуфинал (н)',
  F:   'Финал',
  LF:  'Финал (н)',
  P3:  'Матч за 3-е место',
  LP3: 'Матч за 3-е место (н)'
};

/**
 * Возможные результаты матча.
 * @readonly
 * @enum {string}
 */
export const GameResult = {
  WHITE_WIN: 'white_win',
  BLACK_WIN: 'black_win',
  DRAW: 'draw'
};

/**
 * Условия победы в серии матчей.
 * @readonly
 * @enum {string}
 */
export const WinCondition = {
  GAMES: 'games',
  SCORE: 'score'
};

/**
 * Контроль времени партии.
 * @readonly
 * @enum {string}
 */
export const ControlType = {
  BULLET: 'bullet',
  BLITZ: 'blitz',
  RAPID: 'rapid',
  CLASSIC: 'classic'
};

/**
 * Варианты шахмат.
 * Хранятся в турнире и описывают вид игры.
 * @readonly
 * @enum {string}
 */
export const ChessVariant = {
  CLASSIC: 'classic',   // Классические шахматы
  CHESS960: 'chess960', // Chess960
  MARSEILLAIS: 'marseillais', // Марсельские шахматы
  BUGHOUSE: 'bughouse', // Шведки
  DOUBLES_2X2: 'doubles-2x2', // Парные 2x2
  HAND_AND_BRAIN: 'hand-and-brain', // Голова-Рука
};

/**
 * Человеко-читаемые подписи вариантов шахмат.
 */
export const ChessVariantLabel = {
  [ChessVariant.CLASSIC]: 'Стандарт',
  [ChessVariant.CHESS960]: 'Chess960',
  [ChessVariant.MARSEILLAIS]: 'Марсельские',
  [ChessVariant.BUGHOUSE]: 'Шведки',
  [ChessVariant.DOUBLES_2X2]: 'Парные: 2x2',
  [ChessVariant.HAND_AND_BRAIN]: 'Голова-Рука',
};

/**
 * Типы рейтинга.
 * Используются системой расчёта рейтингов.
 * @readonly
 * @enum {string}
 */
export const RatingType = {
  ELO_T: 'elo_t',
  ELO_960: 'elo_960',
  ELO_MARSEILLAIS: 'elo_marseillais',
  ELO_BUGHOUSE: 'elo_bughouse',
  ELO_DOUBLES_2X2: 'elo_doubles_2x2',
  ELO_HAND_AND_BRAIN: 'elo_hand_and_brain',
};


/**
 * Порядок стадий плей-офф сверху вниз.
 * Используется для сортировки и отображения.
 * @type {string[]}
 */
export const EliminationStageOrder = ['R64', 'R32', 'R16', 'R8', 'QF', 'SF', 'P3', 'F'];

/**
 * Перечисление доступных тай-брейков сезона.
 * @readonly
 * @enum {string}
 */
export const SeasonTiebreak = {
  MOST_WINS: 'mostWins',
  MOST_PODIUMS: 'mostPodiums',
  WINS_BY_GAMES: 'winsByGames',
  BEST_AVERAGE_PLACE: 'bestAveragePlace',
  HEAD_TO_HEAD: 'headToHead',
  BUCHHOLZ_SUMM: 'buchholzSumm',
  PERFORMANCE_SEASON: 'performanceSeason',
};

export const ALLOWED_SEASON_TIEBREAKS = [
  { key: 'mostWins',          label: 'Больше побед (1-е места)' },
  { key: 'mostPodiums',       label: 'Больше подиумов (топ-3)' },
  { key: 'winsByGames',       label: 'Больше побед по партиям' },
  { key: 'bestAveragePlace',  label: 'Лучшее среднее место' },
  { key: 'headToHead',        label: 'Личные встречи' },
  { key: 'buchholzSumm',      label: 'Суммарный коэф. Бухгольца' },
  { key: 'performanceSeason', label: 'Перформанс сезона' },
];

/**
 * Названия коэффициентов тай-брейка турнира.
 * @readonly
 * @enum {string}
 */
export const TieBreakCoefficientLabel = {
  buchholz: 'Бухгольца',
};

/**
 * Статусы сезона.
 * @readonly
 * @enum {string}
 */
export const SeasonStatus = {
  CREATED: 'created',
  ACTIVE: 'active',
  FINISHED: 'finished',
  ARCHIVED: 'archived',
};

/**
 * Режимы начисления очков в сезоне.
 * @readonly
 * @enum {string}
 */
export const SeasonScoringMode = {
  BY_PLACEMENT: 'byPlacement',
  BY_POINTS: 'byPoints',
};

/**
 * Человеко-читаемые подписи режимов начисления очков в сезоне.
 */
export const SeasonScoringModeLabel = {
  [SeasonScoringMode.BY_PLACEMENT]: 'По местам',
  [SeasonScoringMode.BY_POINTS]: 'По очкам',
};

/**
 * Режим начисления при равенстве мест (для byPlacement).
 * @readonly
 * @enum {string}
 */
export const EqualPlacementMode = {
  FIRST: 'first',     // как у первого в группе
  LAST: 'last',       // как у последнего в группе
  AVERAGE: 'average', // среднее значение среди группы
};

/**
 * Человеко-читаемые подписи режима начисления при равенстве мест.
 */

export const EqualPlacementModeLabel = {
  [EqualPlacementMode.FIRST]: 'Как первому',
  [EqualPlacementMode.LAST]: 'Как последнему',
  [EqualPlacementMode.AVERAGE]: 'Среднее значение',
};

/**
 * Способы завершения партии - победа.
 * @readonly
 * @enum {string}
 */
export const TerminationWin = {
  MATE: 'mate',                 // Мат
  RESIGNATION: 'resignation',   // Сдача
  TIME: 'time',                 // По времени
  FORFEIT: 'forfeit',           // Технически
  WITHDRAWAL_WHITE: 'withdrawal-white', // Неявка белых
  WITHDRAWAL_BLACK: 'withdrawal-black', // Неявка чёрных
  WITHDRAWAL_BOTH: 'withdrawal-both', // Неявка обоих
};

/**
 * Способы завершения партии - ничья.
 * @readonly
 * @enum {string}
 */
export const TerminationDraw = {
  AGREED_DRAW: 'agreed_draw',           // Ничья по соглашению
  STALEMATE: 'stalemate',               // Пат
  REPETITION: 'repetition',             // Повторение ходов
  FIFTY_MOVES: 'fifty_moves',           // Правило 50 ходов
  INSUFFICIENT_MATERIAL: 'insufficient_material', // Недостаточно материала
  FORFEIT_DRAW: 'forfeit_draw'          // Технически (ничья)
};

/**
 * Termination-коды, означающие снятие одного или обоих игроков.
 * Используются для единых проверок withdrawal-матчей в UI и модели турнира.
 */
export const TerminationWithdrawal = [
  TerminationWin.WITHDRAWAL_WHITE,
  TerminationWin.WITHDRAWAL_BLACK,
  TerminationWin.WITHDRAWAL_BOTH,
];

/**
 * Termination-коды, означающие технический результат / форфейт.
 * Включают как обычные forfeit, так и снятие игроков.
 */
export const TerminationForfeit = [
  TerminationWin.FORFEIT,
  TerminationDraw.FORFEIT_DRAW,
  ...TerminationWithdrawal,
];

/**
 * Человекочитаемые подписи способов завершения партии - победа.
 * @readonly
 * @enum {string}
 */
export const TerminationWinLabel = {
  [TerminationWin.MATE]: 'Мат',
  [TerminationWin.RESIGNATION]: 'Сдача',
  [TerminationWin.TIME]: 'По времени',
  [TerminationWin.FORFEIT]: 'Технически',
  [TerminationWin.WITHDRAWAL_WHITE]: 'Неявка белых',
  [TerminationWin.WITHDRAWAL_BLACK]: 'Неявка чёрных',
  [TerminationWin.WITHDRAWAL_BOTH]: 'Неявка обоих',
};

/**
 * Человекочитаемые подписи способов завершения партии - ничья.
 * @readonly
 * @enum {string}
 */
export const TerminationDrawLabel = {
  [TerminationDraw.AGREED_DRAW]: 'По соглашению',
  [TerminationDraw.STALEMATE]: 'Пат',
  [TerminationDraw.REPETITION]: 'Повторение ходов',
  [TerminationDraw.FIFTY_MOVES]: '50 ходов',
  [TerminationDraw.INSUFFICIENT_MATERIAL]: 'Недостаточно материала',
  [TerminationDraw.FORFEIT_DRAW]: 'Технически',
};
